import 'swiper/css';
import 'swiper/css/pagination';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Pagination, Navigation, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Button } from '@cfm-media/pwa-ui-components';
import { LoadableStatus } from '../mobx-utils';
import { parseHtml } from '../core/utils';
import { useStore } from '../core/store';

import { ContentWrapper } from '../components/content-wrapper';
import { Loader } from '../components/loader';
import { Modal } from '../components/modal';
import { PageWrapper } from '../components/page-wrapper';
import { urls } from '../urls';

export const imageSliderConfig = {
  modules: [
    //
    Navigation,
    Pagination,
    Autoplay,
  ],
  navigation: true,
  pagination: { dynamicBullets: true },
  slidesPerView: 1,
  spaceBetween: 0,
};

export const HotelDetails = observer(() => {
  const navigate = useNavigate();
  const { serviceIndex } = useParams();
  const { BookingStore } = useStore();
  const [isActive, setIsActive] = useState(false);
  const [hotelIndex, setHotelIndex] = useState(0);

  if (BookingStore?.booking?.status !== LoadableStatus.done) {
    return <Loader />;
  }

  const { hotels, travellers } = BookingStore?.booking?.data;

  let hotel;

  if (
    serviceIndex >= hotels.length ||
    serviceIndex < 0 ||
    !serviceIndex ||
    !hotels[serviceIndex]
  ) {
    return <Navigate to={`${urls.hotelDetails}/0`} replace />;
  } else {
    hotel = hotels[serviceIndex];
  }

  /**
   * Modify the hotel index by a given position (can be negative)
   * @param {number} position
   */
  const modifyHotelIndex = (position) => {
    const index = (hotelIndex + position + hotels.length) % hotels.length;

    setHotelIndex(index);
    navigate(`${urls.hotelDetails}/${index}`);
  };

  // console.log(hotels);

  return (
    <PageWrapper>
      <Swiper
        loop
        className="!rounded-none !m-0"
        {...imageSliderConfig}
        autoplay={{ delay: 5000 }}
      >
        {hotel?.images?.map((image) => (
          <SwiperSlide key={image.url}>
            <div className="w-full h-[400px] relative">
              <img className="w-full h-full object-cover" src={image.url} />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {hotels?.length > 1 ? (
        <div className="fixed bottom-24 lg:bottom-5 left-1/2 lg:ml-[150px] -translate-x-1/2 flex justify-center items-center rounded-full">
          <div
            className="bg-white px-4 py-3 flex justify-center items-center cursor-pointer rounded-full shadow-md hover:bg-gray-200 transition-all"
            onClick={modifyHotelIndex.bind(null, -1)}
          >
            <span className="icon icon-arrow-left" />
          </div>

          <span className="bg-white mx-3 px-6 py-3 text-sm text-center whitespace-nowrap shadow-md rounded-full">
            {hotel?.name}
          </span>

          <div
            className="bg-white px-4 py-3 flex justify-center items-center cursor-pointer rounded-full shadow-md hover:bg-gray-200 transition-all"
            onClick={modifyHotelIndex.bind(null, +1)}
          >
            <span className="icon icon-arrow-right" />
          </div>
        </div>
      ) : null}

      <ContentWrapper>
        <br />

        <div className="text-yellow-500">
          {[...Array(hotel?.stars || 0)]?.map((_, index) => (
            <span className="mr-1 icon icon-star" key={index}></span>
          ))}
        </div>

        <div className="mt-1 text-xl text-brand-1 font-semibold uppercase">
          {hotel.name}
        </div>

        <div className="mt-1 text-gray-600 font-light">{hotel.country}</div>

        <div className="sm:grid sm:grid-cols-2 sm:gap-6">
          <div className="bg-white p-6 mt-6">
            <div className="text-md text-brand-1 font-semibold">
              Ihre gebuchten Leistungen
            </div>

            {hotel.startDate && hotel.endDate && (
              <div className="mt-4 text-sm">
                <strong className="block text-gray-900">Reise-Zeitraum:</strong>
                {`${hotel.startDate} - ${hotel.endDate}`}
              </div>
            )}

            {hotel.roomCategory && hotel.amountOfRooms && (
              <div className="mt-4 text-sm">
                <strong className="block text-gray-900">Zimmer:</strong>
                {`${hotel.roomCategory}, ${hotel.amountOfRooms}x`}
              </div>
            )}

            {hotel.catering && (
              <div className="mt-4 text-sm">
                <strong className="block text-gray-900">Verpflegung:</strong>
                {hotel.catering}
              </div>
            )}

            {hotel.amountOfAdults && (
              <div className="mt-4 text-sm">
                <strong className="block text-gray-900">
                  Anzahl-Erwachsene:
                </strong>
                {hotel.amountOfAdults}
              </div>
            )}

            {hotel.amountOfChildren && (
              <div className="mt-4 text-sm">
                <strong className="block text-gray-900">Anzahl-Kinder:</strong>
                {hotel.amountOfChildren}
              </div>
            )}

            <div className="mt-4 text-sm">
              <strong className="block text-gray-900">Reisende:</strong>
              {hotel?.paxAssignment?.map((traveller) => {
                return (
                  <>
                    <div>
                      <b>{traveller?.lastname}</b>, {traveller?.firstname}
                    </div>
                  </>
                );
              })}
            </div>
          </div>

          <div className="bg-white p-6 mt-6">
            <div className="text-md text-brand-1 font-semibold">
              Kontakt Hotel:
            </div>
            <div className="mt-4 text-sm leading-6">
              {hotel.name}
              <br />
              {hotel.street}
              <br />
              {`${hotel.zip} ${hotel.city}`}
              <br />
              {hotel.country}
              <br />
              Telefon:{' '}
              <a
                className="underline hover:no-underline"
                href={`tel:${hotel.phone}`}
              >
                {hotel.phone}
              </a>
              <br />
              E-Mail:{' '}
              <a
                className="underline hover:no-underline"
                href={`mailto:${hotel.email}`}
              >
                {hotel.email}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 text-sm leading-6">
          <div className="mt-1 text-xl text-brand-1 font-semibold uppercase">
            Beschreibung
          </div>

          <p className="mt-2">{parseHtml(hotel?.description)}</p>
        </div>

        <div className="grid gap-6 grid-cols-2 sm:block py-5">
          {BookingStore?.booking?.data?.hasTransfer && (
            <Button
              title="Zum Transfer"
              variant="primary"
              className="!text-sm sm:mr-6 uppercase"
              onButtonClick={navigate.bind(null, urls.transfer)}
            />
          )}
          {BookingStore?.booking?.data?.flights?.length > 0 && (
            <Button
              className="!text-sm uppercase"
              title="Zum Flug"
              variant="primary"
              onButtonClick={navigate.bind(null, urls.flightDetails)}
            />
          )}
        </div>
      </ContentWrapper>
    </PageWrapper>
  );
});
